import React from 'react';
import { Snackbar } from '../components/snackbar';
import { FormattedMessage } from 'react-intl';
import { call } from './../utilities/http';
import {
    LOADING,
    COMPLETE,
    PAYSTUB_GET_OK,
    PAYSTUBS_GET_OK,
    USER_GET_OK,
    USERS_GET_OK,
    USERS_GET_ADMIN_DETAILS_OK,
    USERS_GET_ADMINS_OK,
    PAYDUES_GET_OK,
    PAYDUE_GET_OK
} from './types';

const defaultState = {
    list: undefined,
    adminDetails: undefined,
    admins: undefined,
    draft: undefined,
    paydues: undefined,
    paydue: undefined
};

/**
 * Loading state mapper.
 * @param {object} state
 * @param {string} action
 */
export default function(state = defaultState, action) {
    switch (action.type) {
        case USER_GET_OK:
            let { list } = state;
            let users = list ? list.filter(u => u.id !== action.data.id) : [];
            return { ...state, list: [...users, action.data] };

        case USERS_GET_OK:
            return { ...state, list: action.data.users, listRole: action.data.role, listInclude: action.data.include };

        case PAYSTUBS_GET_OK:
            return { ...state, paystubs: action.data };

        case PAYSTUB_GET_OK:
            return { ...state, paystub: action.data };

        case PAYDUES_GET_OK:
            return { ...state, paydues: action.data };

        case PAYDUE_GET_OK:
            return { ...state, paydue: action.data };

        case USERS_GET_ADMIN_DETAILS_OK:
            return { ...state, adminDetails: action.data };

        case USERS_GET_ADMINS_OK:
            return { ...state, admins: action.data };

        default:
            return state;
    }
}

export function getUsers(role, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.getUsers'} defaultMessage={'Unable to retrieve company users.'} />;
    const { id } = role;
    return async function(dispatch) {
        dispatch({ type: USERS_GET_OK, data: [] });
        dispatch({ type: LOADING });
        call({ path: `/user?client=${id}` })
            .then(response => {
                dispatch({ type: USERS_GET_OK, data: { users: response, role } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPaystubs(userId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.getStubs'} defaultMessage={'Unable to retrieve pay stubs.'} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${userId}/provider_paystubs` })
            .then(response => {
                dispatch({ type: PAYSTUBS_GET_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPayDues(contractorId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.getPaydues'} defaultMessage={'Unable to retrieve pay dues.'} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/contractor/${contractorId}/paydues` })
            .then(response => {
                dispatch({ type: PAYDUES_GET_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPayDueDetails(payDueId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.getPayDue'} defaultMessage={'Unable to retrieve pay due.'} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/paydue/${payDueId}` })
            .then(response => {
                dispatch({ type: PAYDUE_GET_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPaystub(userId, stubId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.getStubs'} defaultMessage={`Unable to retrieve pay stub.`} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${userId}/provider_paystub/${stubId}` })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response, `${stubId}.pdf`);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function usersAreLoaded(role, listRole, include = undefined, listInclude = undefined) {
    // include[] and listInclude[] match if both exist, arrays have same length, arrays have same contents
    if (include && listInclude) {
        if (include.length !== listInclude.length) return false;
        include.forEach(item => {
            if (!listInclude.includes(item)) return false;
        });
    } else if (include || listInclude) {
        return false;
    }

    if (role && listRole) {
        if (role.id !== listRole.id || role.type !== listRole.type) return false;
    } else if (role || listRole) {
        return false;
    }
    return true;
}

export function getUsersInclusive(role, onSuccess = () => null, include = undefined) {
    let errorMessage = <FormattedMessage id={'error.getUsers'} defaultMessage={'Unable to retrieve company users.'} />;
    const { id, type } = role;

    let withTypes = '';
    if (include && include !== '') {
        withTypes += '?with=';
        if (Array.isArray(include)) {
            withTypes += include.join(',');
        } else {
            withTypes += include;
        }
    }

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/${type}/${id}/users${withTypes}` })
            .then(response => {
                dispatch({ type: USERS_GET_OK, data: { users: response, role, include } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: USERS_GET_OK, data: [] });
                dispatch({ type: COMPLETE });
            });
    };
}

export function getAdminDetails(id, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/admin/${id}` })
            .then(response => {
                dispatch({ type: USERS_GET_ADMIN_DETAILS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getAdmin'} defaultMessage={'Unable to retrieve users.tenant info.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getAdmins(role, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.getUsers'} defaultMessage={'Unable to retrieve company users.'} />;
    const { id } = role;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user?admin=${id}` })
            .then(response => {
                dispatch({ type: USERS_GET_ADMINS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getUser(id, onSuccess = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${id}` })
            .then(response => {
                dispatch({ type: USER_GET_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getUser'} defaultMessage={'Unable to retrieve user info.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function setRole(userId, roleType, clientId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.setUserRole'} defaultMessage={'Unable to set user role.'} />;

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${userId}/role/${roleType}/client/${clientId}`, method: 'PUT' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function unsetRole(userId, roleType, clientId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.unsetUserRole'} defaultMessage={'Unable to remove user role.'} />;

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${userId}/role/${roleType}/client/${clientId}`, method: 'DELETE' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}



// WEBPACK FOOTER //
// ./src/reducers/users.jsx